/*
|--------------------------------------------------------------------------
| Hook & Helpers: useProductVariants.js
| Author: Sayed Ahmed
| Description: Manages logic for product variants including adding/removing
|              variants dynamically and syncing with selected attributes.
|--------------------------------------------------------------------------
*/

import { useEffect } from "react";
import { useFieldArray } from "react-hook-form";
import {
  generateCombinations,
  mapValueToAttribute,
  generateVariantObject,
  isDuplicateVariant,
  generateSku,
} from "../utils/variantUtils";

/**
 * Custom hook for managing product variant form fields.
 */
export function useProductVariants({ control, skuCode, setValue, initialVariants = [] }) {
  const {
    fields: variantFields,
    append,
    remove,
    update,
  } = useFieldArray({
    control,
    name: "variants",
    keyName: "_key",
  });

  // Populate initial variant fields
  useEffect(() => {
    if (initialVariants.length) {
      remove();
      initialVariants.forEach((variant) => append(variant));
    }
  }, [initialVariants]);

  // Auto-generate SKU for each variant when data changes
  useEffect(() => {
    variantFields.forEach((variant, index) => {
      if (Array.isArray(variant.attribute_values_display)) {
        const fullSku = generateSku(skuCode, variant.attribute_values_display);
        // Only update if the variant doesn't have a custom SKU or if it matches the base SKU
        if (!variant.sku || variant.sku == skuCode) {
          setValue(`variants.${index}.sku`, fullSku);
        }
      }
      
      // Set default discount values if not set
      if (!variant.discount && variant.discount !== 0) {
        setValue(`variants.${index}.discount`, 0);
      }
      if (!variant.discount_type) {
        setValue(`variants.${index}.discount_type`, "fixed");
      }
    });
  }, [variantFields, skuCode, setValue]);

  return { variantFields, append, remove, update };
}

/**
 * Toggle attribute selection (checkbox).
 */
export function handleToggleAttribute(attributeId, isChecked, allAttributes, currentAttributes, onUpdate) {
  const newAttributes = { ...currentAttributes };

  if (isChecked) {
    const attribute = allAttributes.find(attr => attr.id == attributeId);
    if (attribute) {
      newAttributes[attributeId] = {
        name: attribute.name,
        attribute: attribute,
        values: [],
      };
    }
  } else {
    delete newAttributes[attributeId];
  }

  onUpdate(newAttributes);

  return { ...newAttributes };
}

/**
 * Toggle specific attribute value (checkbox).
 */
export function handleToggleAttributeValue(attributeId, valueId, isChecked, allAttributes, currentAttributes, onUpdate) {
  const newAttributes = { ...currentAttributes };

  if (!newAttributes[attributeId]) return currentAttributes;

  const values = newAttributes[attributeId].values || [];
  const existing = values.find(v => v.id == valueId);

  if (isChecked && !existing) {
    const attribute = allAttributes.find(attr => attr.id == attributeId);
    const value = attribute?.values.find(val => val.id == valueId);
    if (value) {
      newAttributes[attributeId] = {
        ...newAttributes[attributeId],
        values: [...values, value],
      };
    }
  }

  if (!isChecked && existing) {
    newAttributes[attributeId] = {
      ...newAttributes[attributeId],
      values: values.filter(val => val.id !== valueId),
    };
  }

  onUpdate(newAttributes);

  return { ...newAttributes }; // نسخ جديد لضمان التحديث
}

/**
 * Rebuild variant fields when selected attributes/values change.
 */
export function rebuildVariantsFromAttributes({
  newAttributes,
  watch,
  variantFields,
  append,
  remove,
  keepExisting = false,
}) {
  const selectedAttributes = Object.values(newAttributes);
  if (selectedAttributes.length == 0) {
    remove();
    return newAttributes;
  }

  const valueToAttrMap = mapValueToAttribute(newAttributes);
  const attributeValues = selectedAttributes.map((attr) => attr.values);
  const combinations = generateCombinations(attributeValues);

  remove();

  const basePrice = watch("price");
  const baseStock = watch("stock");
  const baseSku = watch("sku");
  const baseDiscount = watch("discount") || 0;
  const baseDiscountType = watch("discount_type") || "fixed";

  combinations.forEach((combo) => {
    const existing = variantFields.find((variant) =>
      isDuplicateVariant(combo, variant, valueToAttrMap)
    );

    const newVariant = generateVariantObject(
      combo,
      valueToAttrMap,
      newAttributes,
      baseSku,
      basePrice,
      baseStock,
      baseDiscount,
      baseDiscountType
    );

    append(keepExisting && existing ? { ...newVariant, ...existing } : newVariant);
  });

  return newAttributes;
}

/**
 * Normalize backend product variant structure for form use.
 */
export function normalizeVariantsAndAttributes(product) {
  const attributesMap = {};
  const variants = [];

  (product.variants || []).forEach((variant) => {
    const attribute_values_display = (variant.attribute_values || []).map((val) => {
      const attrId = val.attribute_id;

      if (!attributesMap[attrId]) {
        attributesMap[attrId] = {
          name: val.attribute?.name || "",
          values: [],
        };
      }

      const alreadyExists = attributesMap[attrId].values.find((v) => v.id == val.id);
      if (!alreadyExists) {
        attributesMap[attrId].values.push({
          id: val.id,
          value: val.value,
          hex: val.hex,
        });
      }

      return {
        id: val.id,
        attribute_id: attrId,
        attribute_name: val.attribute?.name || "",
        value: val.value,
        hex: val.hex,
      };
    });

    const attribute_values = attribute_values_display.map((val) => val.id);

    variants.push({
      id: variant.id, // Passed through for submission; not validated here
      sku: variant.sku,
      price: variant.price,
      discount: variant.discount || 0,
      discount_type: variant.discount_type || "fixed",
      stock: variant.stock,
      images: variant.images || [],
      attribute_values,
      attribute_values_display,
    });
  });

  return { attributesMap, variants };
}
